import { z } from 'zod';
import { Timestamp } from 'firebase/firestore'; // Add this import
import { MemberRoleSchema } from '@/types/schema/commonSchema'; // Import MemberRoleSchema
import {
  optionalEmailFieldSchema,
  // genericNameFieldSchema, // Replaced by optional version
  // phoneFieldSchema, // Replaced by optional version
  optionalGenericNameFieldSchema, // New
  optionalPhoneFieldSchema // New
} from '@/utils/validationRules';

// Reusable Firestore Timestamp schema
// Uses z.preprocess to convert various Firestore timestamp formats to Date objects or null
export const firestoreTimestampSchema = z.preprocess((arg) => {
  if (!arg) return null;
  if (arg instanceof Timestamp) {
    return arg.toDate();
  }
  // Check for Firestore-like raw object structure {_seconds, _nanoseconds}
  if (typeof arg === 'object' && arg !== null && 
      '_seconds' in arg && typeof (arg as any)._seconds === 'number' &&
      '_nanoseconds' in arg && typeof (arg as any)._nanoseconds === 'number') {
    return new Date((arg as any)._seconds * 1000 + (arg as any)._nanoseconds / 1000000);
  }
  // Handle potential legacy string or number timestamps
  if (typeof arg === 'string' || typeof arg === 'number') {
    const date = new Date(arg);
    // Check if the date is valid
    if (!isNaN(date.getTime())) {
      return date;
    }
  }
  // If it's an object but not a recognized timestamp structure, or any other unhandled case
  // Return null to be caught by z.date().nullable()
  return null; 
}, z.date().nullable().optional());

// Placeholder for permission objects, to be refined
const permissionObjectSchema = z.object({
  name: z.string(),
  checked: z.boolean(),
  id: z.string(),
  description: z.string().optional(), // Seen in some member permissions
}).passthrough(); // Allow other fields for now

export const userSchema = z.object({
  displayName: z.string().trim().nullable().optional(), // Keep as simple string, no min length enforced by default
  email: optionalEmailFieldSchema.nullable(),
  status: z.enum(["active", "invited", "removed", "pending_verification", "archived"]).optional(), // More comprehensive status list
  avatarUrl: z.string().url().or(z.literal('')).nullable().optional(), // Allow empty string
  avatarInitials: z.string().trim().nullable().optional(),
  firstName: optionalGenericNameFieldSchema("First name").nullable(), // Use optional version
  lastName: optionalGenericNameFieldSchema("Last name").nullable(), // Use optional version
  phone: optionalPhoneFieldSchema.nullable(), // Use optional version
  role: MemberRoleSchema.nullable().optional(), // Use MemberRoleSchema

  customClaims: z.object({
    role: z.string().optional(),
  }).nullable().optional(),

  permissions: z.array(permissionObjectSchema).nullable().optional(),
  
  portfolios: z.array(z.string()).nullable().optional(), // Array of portfolio IDs
  businesses: z.array(z.string()).nullable().optional(), // Array of business IDs

  createdAt: firestoreTimestampSchema,
  updatedAt: firestoreTimestampSchema,
  lastSeen: firestoreTimestampSchema,
  termsLastVerified: firestoreTimestampSchema, // Optional based on data

  // Fields like userId and id are typically document IDs and not part of the document's data structure itself.
  // isCurrentUser is a client-side runtime flag, not typically stored in Firestore.
}).passthrough(); // Allow other fields not explicitly defined, useful during initial schema creation

export type UserDataInput = z.infer<typeof userSchema>; // Represents data structure within Firestore

// Schema for the user object once it's fetched and has an ID attached (typically firebaseUser.uid)
export const userOutputSchema = userSchema.extend({
  id: z.string(), // Document ID
  // userId: z.string().optional(), // If userId is distinct from id and also part of schema
});
export type User = z.infer<typeof userOutputSchema>; // This type includes the 'id'
